import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useModalAnimation } from '@/hooks/useModalAnimation';

interface Props {
  elegiveis: Funcionario[];
  pontosPorFuncionario: Record<string, number>;
  onClose: () => void;
}

interface AlertConfig {
  email: string;
  ativo: boolean;
  enviarAoAtingir: boolean;
  enviarResumoSemanal: boolean;
}

const STORAGE_KEY = 'siadap_alert_config';

function loadConfig(): AlertConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as AlertConfig;
  } catch { /* ignore */ }
  return { email: '', ativo: false, enviarAoAtingir: true, enviarResumoSemanal: false };
}

function saveConfig(cfg: AlertConfig) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
}

export default function AlertasAutomaticosModal({ elegiveis, pontosPorFuncionario, onClose }: Props) {
  const { animating, handleClose } = useModalAnimation(onClose);
  const [config, setConfig] = useState<AlertConfig>(loadConfig);
  const [sending, setSending] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveConfig(config);
  }, [config]);

  function update(partial: Partial<AlertConfig>) {
    setConfig((prev) => ({ ...prev, ...partial }));
    setSaved(false);
  }

  async function handleTestar() {
    if (!config.email) { setError('Introduz um email válido.'); return; }
    setError('');
    setSending(true);
    try {
      const payload = {
        destinatario: config.email,
        elegiveis: elegiveis.slice(0, 3).map((f) => ({
          nome: f.nome,
          carreira: f.carreira,
          nivelAtual: f.nivelAtual,
          proximoNivel: f.nivelAtual + 1,
          pontosAtuais: pontosPorFuncionario[f.id] ?? 0,
          unidadeOrganica: f.unidadeOrganica,
        })),
        ciclo: '2023–2024',
        automatico: false,
      };
      const { error: fnErr } = await supabase.functions.invoke('send-progressao-alert', { body: payload });
      if (fnErr) throw new Error(fnErr.message);
      setTestSent(true);
      setTimeout(() => setTestSent(false), 4000);
    } catch (e) {
      setError(String(e));
    } finally {
      setSending(false);
    }
  }

  function handleSalvar() {
    saveConfig(config);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={handleClose}>
      <div className={`bg-white rounded-2xl border border-zinc-100 w-full max-w-lg mx-4 overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-zinc-900">
              <i className="ri-notification-3-line text-white text-base" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-zinc-900">Alertas Automáticos</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Notificações por email quando funcionários atingem o limiar</p>
            </div>
          </div>
          <button onClick={handleClose} className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Estado atual */}
          <div className={`flex items-center justify-between p-4 rounded-xl border ${config.ativo ? 'bg-emerald-50 border-emerald-100' : 'bg-zinc-50 border-zinc-100'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${config.ativo ? 'bg-emerald-100 text-emerald-600' : 'bg-zinc-100 text-zinc-400'}`}>
                <i className={`${config.ativo ? 'ri-notification-3-fill' : 'ri-notification-off-line'} text-sm`} />
              </div>
              <div>
                <p className={`text-[13px] font-semibold ${config.ativo ? 'text-emerald-800' : 'text-zinc-600'}`}>
                  Alertas {config.ativo ? 'Ativos' : 'Desativados'}
                </p>
                <p className="text-[11px] text-zinc-400">
                  {config.ativo ? 'Receberás notificações do SIADAP para RH quando funcionários atingirem o limiar' : 'Ativa para receber notificações automáticas do SIADAP para RH'}
                </p>
              </div>
            </div>
            <button
              onClick={() => update({ ativo: !config.ativo })}
              className={`whitespace-nowrap relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${config.ativo ? 'bg-emerald-500' : 'bg-zinc-200'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.ativo ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Email */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-600 mb-1.5">Email de destino</label>
            <input
              type="email"
              value={config.email}
              onChange={(e) => update({ email: e.target.value })}
              placeholder="rh@camara.pt"
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
            />
          </div>

          {/* Opções */}
          <div className="space-y-3">
            <p className="text-[12px] font-semibold text-zinc-600">Quando enviar</p>
            {[
              {
                key: 'enviarAoAtingir' as const,
                label: 'Ao atingir o limiar',
                desc: 'Envia um email imediatamente quando um funcionário atinge os pontos necessários para progressão',
                icon: 'ri-flashlight-line',
              },
              {
                key: 'enviarResumoSemanal' as const,
                label: 'Resumo semanal',
                desc: 'Envia um resumo semanal com todos os funcionários elegíveis (requer configuração de cron job no Supabase)',
                icon: 'ri-calendar-schedule-line',
              },
            ].map((opt) => (
              <label key={opt.key} className="flex items-start gap-3 p-3.5 rounded-xl border border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={config[opt.key]}
                  onChange={(e) => update({ [opt.key]: e.target.checked })}
                  className="mt-0.5 cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <i className={`${opt.icon} text-zinc-400 text-sm`} />
                    <p className="text-[13px] font-semibold text-zinc-700">{opt.label}</p>
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Info sobre funcionários elegíveis */}
          {elegiveis.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3.5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                <p className="text-[12px] font-semibold text-emerald-800">{elegiveis.length} funcionário(s) elegíveis agora</p>
              </div>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {elegiveis.slice(0, 5).map((f) => (
                  <p key={f.id} className="text-[11px] text-emerald-700">
                    {f.nome} · {f.carreira} · <strong>{(pontosPorFuncionario[f.id] ?? 0).toFixed(1)} pts</strong>
                  </p>
                ))}
                {elegiveis.length > 5 && (
                  <p className="text-[11px] text-emerald-500">+{elegiveis.length - 5} mais...</p>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-[12px] px-3.5 py-2.5 rounded-xl">
              <i className="ri-error-warning-line text-sm flex-shrink-0" />
              {error}
            </div>
          )}

          {testSent && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700 text-[12px] px-3.5 py-2.5 rounded-xl">
              <i className="ri-checkbox-circle-line text-sm flex-shrink-0" />
              Email de teste enviado com sucesso!
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between gap-3">
          <button
            onClick={handleTestar}
            disabled={sending || !config.email || elegiveis.length === 0}
            className="whitespace-nowrap flex items-center gap-2 text-[13px] font-medium border border-zinc-200 text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 px-4 py-2.5 rounded-lg transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? <i className="ri-loader-4-line text-sm animate-spin" /> : <i className="ri-send-plane-line text-sm" />}
            Enviar Teste
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handleClose} className="whitespace-nowrap text-[13px] font-medium text-zinc-500 hover:text-zinc-700 px-4 py-2.5 rounded-lg transition-colors cursor-pointer">
              Fechar
            </button>
            <button
              onClick={handleSalvar}
              className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              {saved ? <><i className="ri-checkbox-circle-line text-sm" />Guardado!</> : <><i className="ri-save-line text-sm" />Guardar</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
